import { SecureImage } from '@/components/ui/SecureImage';
import { cn, getNameInitials } from '@/lib/utils';
import type { StudentStatus } from '@/types/student';
import { STUDENT_STATUS_LABEL, STUDENT_STATUS_PAINT } from '../constants';
import { studentsApi } from '../api/students.api';

export interface StudentAvatarProps {
  fullName: string;
  status: StudentStatus;
  className?: string;
  /**
   * The child's public id, and whether they have a photograph.
   *
   * Both, or neither: without the uuid there is nothing to ask for, and asking
   * for a photograph that is not there is a 404 per row. A caller that has no
   * answer to `hasPhoto` simply omits it and gets the initials.
   */
  uuid?: string;
  hasPhoto?: boolean;
}

/**
 * A child on the roll, as a face.
 *
 * Their photograph when the record carries one, and their initials when it does
 * not — the same fallback the console's own identity block draws, through the
 * same helper (CLAUDE.md §3). It is not decoration: forty rows of text are read
 * one line at a time, and a mark against each is what lets somebody find the
 * row they were on again after scrolling.
 *
 * The bytes come through `SecureImage` rather than an `<img src>`, because an
 * uploaded file is private and has no public URL (§11) — it fetches them with
 * the access token like any other request. A row is only asked to do that when
 * `hasPhoto` says there is something to fetch, so a roll of children with no
 * photographs makes no requests at all.
 *
 * The ring around it carries the standing, so the eye picks a suspended record
 * out of a page without reading the badge two columns away. The badge stays —
 * colour alone is never the only thing saying it (§7).
 */
export function StudentAvatar({
  fullName,
  status,
  className,
  uuid,
  hasPhoto,
}: StudentAvatarProps) {
  const paint = STUDENT_STATUS_PAINT[status];

  /**
   * Fills the frame rather than sitting inside it, so it carries its own
   * colour. The gradient belongs to the initials and not to the frame around
   * them: on the frame it would show around a photograph that does not fill
   * every corner, and read as a coloured border nobody asked for.
   */
  const initials = (
    <span className="absolute inset-0 grid place-items-center bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[12px] font-black text-white">
      {getNameInitials(fullName)}
      <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-sheen" />
    </span>
  );

  return (
    <span
      className={cn(
        'relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-2xl',
        // Plain, and only ever seen while a photograph is loading — whatever is
        // drawn on top brings its own colour.
        'bg-surface-2',
        'shadow-md shadow-indigo-500/20 ring-2 ring-offset-2 ring-offset-white dark:ring-offset-[#111c2e]',
        paint.ring,
        className,
      )}
      // The initials are decoration over the name in the cell beside it; the
      // standing is announced from the badge, not from a colour.
      title={`${fullName} — ${STUDENT_STATUS_LABEL[status]}`}
      aria-hidden
    >
      {uuid && hasPhoto ? (
        <SecureImage
          path={studentsApi.photoPath(uuid)}
          alt=""
          // Cropped rather than letterboxed, and filling the frame: a head
          // photo in a 40px square reads as a face only if it fills it, and a
          // fitted one would leave the frame showing at two edges.
          className="absolute inset-0 h-full w-full object-cover"
          fallback={initials}
        />
      ) : (
        initials
      )}
    </span>
  );
}
